import {
  Box,
  Button,
  InputBase,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  SearchIcon,
  FilterIcon,
  MessageSquareIcon,
  MailIcon,
  SendIcon,
  InstagramIcon,
  CheckIcon,
  CheckCheckIcon,
  ChevronDownIcon,
  XIcon,
  LucideFacebook,
} from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";

import { ChannelType } from "./Conversation";

interface Contact {
  name: string;
  avatar: string;
}

interface LastMessage {
  text: string;
  time: string;
  isOutgoing?: boolean;
  status?: "sent" | "delivered" | "read" | string;
}

export interface Conversation {
  id: string;
  contact: Contact;
  lastMessage: LastMessage;
  channel: ChannelType;
  unreadCount?: number;
}

interface InboxProps {
  conversations: Conversation[];
  activeConversation: string;
  setActiveConversation: (id: string) => void;
  isLoading?: boolean;
}

const PAGE_SIZE = 50; // Number of conversations to display per page

const Inbox: React.FC<InboxProps> = ({
  conversations = [],
  activeConversation,
  setActiveConversation,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery("(max-width:1280px)");

  // Search state
  const [searchInputValue, setSearchInputValue] = useState(""); // Current text in input field
  const [searchQuery, setSearchQuery] = useState(""); // Active search query (after pressing search)
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Debounced search function for better performance
  const executeSearch = useCallback(() => {
    setIsSearching(true);
    setSearchQuery(searchInputValue);

    // Simulate search delay for UX
    setTimeout(() => {
      setIsSearching(false);
    }, 300);
  }, [searchInputValue]);

  // Search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      executeSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchInputValue("");
    setSearchQuery("");
  };

  // Filter conversations based on search query
  const filteredConversations = searchQuery
    ? conversations.filter(
        (conv) =>
          conv.contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          conv.lastMessage.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  // Get the current page of conversations
  const paginatedConversations = filteredConversations.slice(0, currentPage * PAGE_SIZE);

  // Check if there are more conversations to load
  const hasMore = paginatedConversations.length < filteredConversations.length;

  // Function to load more conversations
  const loadMore = () => {
    setLoadingMore(true);
    // Simulate loading delay for smoother UX
    setTimeout(() => {
      setCurrentPage((prev) => prev + 1);
      setLoadingMore(false);
    }, 300);
  };

  const getChannelIcon = (channel: ChannelType) => {
    switch (channel) {
      case "whatsapp":
        return <MessageSquareIcon size={16} style={{ color: "#10B981" }} />; // text-green-500
      case "email":
        return <MailIcon size={16} style={{ color: "#3B82F6" }} />; // text-blue-500
      case "telegram":
        return <SendIcon size={16} style={{ color: "#60A5FA" }} />; // text-blue-400
      case "instagram":
        return <InstagramIcon size={16} style={{ color: "#8B5CF6" }} />; // text-purple-500

      case "facebook":
        return <LucideFacebook size={16} style={{ color: "#3B82F6" }} />; // text-blue-500
      default:
        return null;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <CheckIcon size={14} style={{ color: "#9CA3AF" }} />; // text-gray-400
      case "delivered":
        return <CheckCheckIcon size={14} style={{ color: "#9CA3AF" }} />; // text-gray-400
      case "read":
        return <CheckCheckIcon size={14} style={{ color: "#3B82F6" }} />; // text-blue-500
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%", // Always 100% of parent container
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        overflow: "hidden", // Critical to prevent overflow
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: isTablet ? 1.5 : 2,
          borderBottom: "1px solid #E5E7EB",
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        {/* Search Box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#F3F4F6", // gray-100
            borderRadius: 1,
            px: 1.5,
            py: isTablet ? 0.75 : 1,
            position: "relative",
            border: searchQuery ? "1px solid #3B82F6" : "1px solid transparent",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={executeSearch}
          >
            {isSearching ? (
              <CircularProgress size={18} sx={{ color: "#6B7280", mr: 1 }} />
            ) : (
              <SearchIcon size={18} style={{ color: "#6B7280", marginRight: 8 }} />
            )}
          </Box>

          <InputBase
            placeholder="Cerca conversazioni..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              backgroundColor: "transparent",
              border: "none",
              width: "100%",
              fontSize: "0.875rem", // text-sm
              "& .MuiInputBase-input": { p: 0 },
            }}
            inputProps={{ "aria-label": "search conversations" }}
          />

          {searchInputValue && (
            <Box
              onClick={clearSearch}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#6B7280",
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: "#E5E7EB",
                },
              }}
            >
              <XIcon size={16} />
            </Box>
          )}
        </Box>

        {/* Search Hint/Status */}
        {searchQuery && (
          <Box
            sx={{
              mt: 1,
              px: 1,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              backgroundColor: "#EFF6FF", // Light blue background
              borderRadius: 1,
              fontSize: "0.75rem",
              color: "#3B82F6",
            }}
          >
            <SearchIcon size={12} style={{ marginRight: 4 }} />
            <Box component="span" sx={{ flexGrow: 1 }}>
              {`Showing results for "${searchQuery}"`}
            </Box>
            <Box
              component="span"
              onClick={clearSearch}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "0.7rem",
                "&:hover": { color: "#2563EB" },
              }}
            >
              Clear
            </Box>
          </Box>
        )}

        {/* Inbox Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: searchQuery ? 1 : isTablet ? 1.5 : 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: isTablet ? "0.95rem" : "1rem", // Slightly smaller on tablets
            }}
          >
            {searchQuery ? "Search Results" : "Inbox"}
            {filteredConversations.length > 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  color: "#6B7280",
                  fontWeight: "normal",
                  fontSize: "0.875rem",
                }}
              >
                ({paginatedConversations.length}/{filteredConversations.length})
              </Typography>
            )}
          </Typography>
          <Button
            variant="text"
            sx={{
              textTransform: "none",
              color: "#6B7280",
              fontSize: "0.875rem",
              minWidth: "auto", // Prevent button from taking too much space
              px: isTablet ? 1 : 1.5, // Smaller padding on tablet
            }}
            startIcon={<FilterIcon size={14} style={{ marginRight: 4 }} />}
          >
            Filter
          </Button>
        </Box>
      </Box>

      {/* Conversation List - This will scroll */}
      <Box
        sx={{
          flexGrow: 1, // Take all available space
          overflowY: "auto",
          overflowX: "hidden", // Prevent horizontal scroll
          display: "flex",
          flexDirection: "column",
          WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
        }}
      >
        {isLoading || isSearching ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              p: 4,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#3B82F6" }} />
            <Typography sx={{ mt: 2, color: "#6B7280", fontSize: "0.875rem" }}>
              {isSearching ? "Searching conversations..." : "Loading conversations..."}
            </Typography>
          </Box>
        ) : paginatedConversations.length === 0 ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              color: "#6B7280",
              fontSize: "0.875rem",
            }}
          >
            {searchQuery ? `No conversations match "${searchQuery}"` : "No conversations found"}
          </Box>
        ) : (
          <>
            {paginatedConversations.map((conv) => (
              <Box
                key={conv.id}
                onClick={() => setActiveConversation(conv.id)}
                sx={{
                  p: isTablet ? 1.5 : 2,
                  borderBottom: "1px solid #F3F4F6",
                  cursor: "pointer",
                  transition: "background-color 200ms",
                  backgroundColor: activeConversation === conv.id ? "#EFF6FF" : "transparent",
                  "&:hover": {
                    backgroundColor: activeConversation === conv.id ? "#EFF6FF" : "#F9FAFB",
                  },
                  flexShrink: 0, // Prevent conversation items from shrinking
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  {/* Avatar with Channel Icon */}
                  <Box
                    sx={{
                      position: "relative",
                      mr: isTablet ? 1.5 : 2,
                      flexShrink: 0, // Prevent avatar from shrinking
                    }}
                  >
                    <Avatar
                      src={conv.contact.avatar}
                      alt={conv.contact.name}
                      sx={{
                        width: isTablet ? 40 : 48,
                        height: isTablet ? 40 : 48,
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        backgroundColor: "white",
                        borderRadius: "50%",
                        width: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    >
                      {getChannelIcon(conv.channel)}
                    </Box>
                  </Box>

                  {/* Conversation Details */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0, // Critical for text truncation
                      width: "100%", // Ensure the box takes full width
                      overflow: "hidden", // Prevent overflow
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%", // Ensure this takes full width
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 500,
                          color: "#111827", // text-gray-900
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: isTablet ? "65%" : "70%", // Control the width
                          fontSize: isTablet ? "0.825rem" : "0.875rem", // Smaller on tablet
                        }}
                      >
                        {conv.contact.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6B7280",
                          flexShrink: 0, // Prevent time from shrinking
                          fontSize: isTablet ? "0.7rem" : "0.75rem", // Smaller on tablet
                        }}
                      >
                        {conv.lastMessage.time}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 0.5, // Reduced margin on tablet
                        width: "100%", // Ensure this takes full width
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#4B5563", // text-gray-600
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: isTablet ? "80%" : "85%", // Control the width
                          fontSize: isTablet ? "0.75rem" : "0.8125rem", // Smaller on tablet
                        }}
                      >
                        {conv.lastMessage.isOutgoing && (
                          <Box
                            component="span"
                            sx={{
                              mr: 0.5,
                              display: "inline-flex",
                              verticalAlign: "middle",
                            }}
                          >
                            {getStatusIcon(conv.lastMessage.status)}
                          </Box>
                        )}
                        {conv.lastMessage.text}
                      </Typography>
                      {conv.unreadCount && conv.unreadCount > 0 && (
                        <Box
                          sx={{
                            backgroundColor: "#3B82F6",
                            color: "white",
                            fontSize: "0.7rem", // text-xs
                            borderRadius: "50%",
                            width: isTablet ? 18 : 20,
                            height: isTablet ? 18 : 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0, // Prevent badge from shrinking
                            ml: 0.5, // Small left margin
                          }}
                        >
                          {conv.unreadCount}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Load more button */}
            {hasMore && (
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  borderTop: "1px solid #F3F4F6",
                }}
              >
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  variant="text"
                  color="primary"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    py: 0.5,
                  }}
                  startIcon={
                    loadingMore ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ChevronDownIcon size={16} />
                    )
                  }
                >
                  {loadingMore
                    ? "Loading..."
                    : `Load more (${
                        filteredConversations.length - paginatedConversations.length
                      } remaining)`}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Inbox;
